/** DIVETTA MILANO R7 — canonical media-state + visibility-aware video owner. */
(() => {
  'use strict';

  const KEY = '__DivettaR7Media';
  window[KEY]?.destroy?.();

  const controller = new AbortController();
  const { signal } = controller;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;
  const ratios = new Map();
  let observer = null;
  let videoFrame = 0;
  let stateFrame = 0;
  let unsubscribeStateFrame = null;

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const smooth = (value) => {
    const t = clamp(value);
    return t * t * (3 - (2 * t));
  };

  const allVideos = () => Array.from(document.querySelectorAll([
    'video[data-r7-state-video]',
    'video[data-r7-autoplay-video]',
    '[data-r7-autoplay-media] video',
    'video[autoplay][muted][playsinline]'
  ].join(',')));

  const autonomousStages = () => Array.from(document.querySelectorAll('[data-r7-media-stage][data-r7-media-mode="autonomous"]'));

  const capabilities = () => {
    if (window.DivettaZorge?.capabilities) return window.DivettaZorge.capabilities;
    const memory = Number(navigator.deviceMemory || 0);
    const processors = Number(navigator.hardwareConcurrency || 0);
    const saveData = Boolean(connection?.saveData);
    const lowCapability = (memory > 0 && memory <= 4) || (processors > 0 && processors <= 4);
    const canAnimate = !reduceMotion.matches && !saveData && !lowCapability;
    return { canAnimate, canAutoplay: canAnimate };
  };

  const stateAllowsPlayback = (video) => {
    const state = video.closest('[data-r7-media-state]');
    if (!state) return true;
    return state.classList.contains('is-dominant') && state.getAttribute('aria-hidden') !== 'true';
  };

  const isLcpMedia = (video) => {
    const owner = video.closest('[data-r7-lcp-media]');
    return Boolean(owner && owner.getAttribute('data-r7-lcp-media') !== 'false');
  };

  const canPlay = (video) => {
    if (document.visibilityState === 'hidden') return false;
    if (!capabilities().canAutoplay) return false;
    if (!stateAllowsPlayback(video)) return false;
    return (ratios.get(video) || 0) >= .48;
  };

  const pause = (video) => {
    if (!video.paused) video.pause();
    if (!isLcpMedia(video) && !stateAllowsPlayback(video)) video.preload = 'none';
  };

  const syncVideos = () => {
    videoFrame = 0;
    const videos = allVideos();
    const candidates = videos
      .filter(canPlay)
      .sort((a, b) => (ratios.get(b) || 0) - (ratios.get(a) || 0));
    const active = candidates[0] || null;

    videos.forEach((video) => {
      video.muted = true;
      video.playsInline = true;
      if (video === active) {
        video.preload = 'metadata';
        if (video.paused) {
          const promise = video.play();
          promise?.catch?.(() => {});
        }
      } else {
        pause(video);
      }
    });
  };

  const requestVideoSync = () => {
    if (videoFrame) return;
    videoFrame = window.requestAnimationFrame(syncVideos);
  };

  const viewportProgress = (stage) => {
    const rect = stage.getBoundingClientRect();
    const viewport = Math.max(window.innerHeight, 1);
    return clamp(((viewport * .82) - rect.top) / Math.max(rect.height + (viewport * .42), 1));
  };

  const stateBlend = (progress, count) => {
    if (count <= 1 || progress >= 1) {
      const index = count <= 1 ? 0 : count - 1;
      return { from: index, to: index, mix: 0, dominant: index };
    }
    const scaled = clamp(progress) * count;
    const from = Math.min(count - 1, Math.floor(scaled));
    const local = clamp(scaled - from);
    if (from >= count - 1 || local <= .74) return { from, to: from, mix: 0, dominant: from };
    const to = Math.min(count - 1, from + 1);
    const mix = smooth((local - .74) / .26);
    return { from, to, mix, dominant: mix < .5 ? from : to };
  };

  const setAutonomousStage = (stage) => {
    const states = Array.from(stage.querySelectorAll(':scope > [data-r7-media-state]'));
    if (!states.length) return;
    const progress = capabilities().canAnimate ? viewportProgress(stage) : 0;
    const blend = stateBlend(progress, states.length);
    const previous = stage.dataset.r7MediaActive;

    stage.dataset.r7MediaMounted = 'true';
    stage.dataset.r7MediaActive = String(blend.dominant);
    stage.style.setProperty('--r7-media-progress', progress.toFixed(4));

    states.forEach((state, index) => {
      let opacity = 0;
      let shift = 1.75;
      let scale = 1.015;
      if (index === blend.from) {
        opacity = blend.to === blend.from ? 1 : 1 - blend.mix;
        shift = blend.to === blend.from ? 0 : blend.mix * -1.25;
        scale = 1 + (blend.mix * .012);
      } else if (index === blend.to) {
        opacity = blend.mix;
        shift = (1 - blend.mix) * 1.75;
        scale = 1.015 - (blend.mix * .015);
      }
      const dominant = index === blend.dominant;
      state.style.setProperty('--r7-media-opacity', opacity.toFixed(4));
      state.style.setProperty('--r7-media-shift', `${shift.toFixed(3)}rem`);
      state.style.setProperty('--r7-media-scale', scale.toFixed(4));
      state.classList.toggle('is-active', dominant);
      state.classList.toggle('is-dominant', dominant);
      state.classList.toggle('is-leaving', index === blend.from && blend.to !== blend.from);
      state.classList.toggle('is-entering', index === blend.to && blend.to !== blend.from);
      state.setAttribute('aria-hidden', dominant ? 'false' : 'true');
      state.toggleAttribute('inert', !dominant);
    });

    stage.querySelectorAll('[data-r7-media-meter]').forEach((meter, index) => {
      meter.classList.toggle('is-active', index === blend.dominant);
    });

    if (previous !== stage.dataset.r7MediaActive) requestVideoSync();
  };

  const syncAutonomousStages = () => {
    stateFrame = 0;
    /* The Home route has its own route-scoped choreography owner. */
    if (document.body?.dataset.r7Route === 'home') return;
    autonomousStages().forEach(setAutonomousStage);
  };

  const requestStateSync = () => {
    if (stateFrame) return;
    stateFrame = window.requestAnimationFrame(syncAutonomousStages);
  };

  const mount = () => {
    observer?.disconnect();
    ratios.clear();

    const videos = allVideos();
    videos.forEach((video) => {
      video.muted = true;
      video.playsInline = true;
      video.removeAttribute('autoplay');
      video.preload = isLcpMedia(video) ? 'metadata' : 'none';
    });

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => ratios.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0));
        requestVideoSync();
      }, { threshold: [0, .18, .48, .72, .92] });
      videos.forEach((video) => observer.observe(video));
    } else {
      videos.forEach((video) => ratios.set(video, 1));
    }

    requestStateSync();
    requestVideoSync();
  };

  document.addEventListener('r7:media-state-change', requestVideoSync, { signal });
  document.addEventListener('visibilitychange', requestVideoSync, { signal });
  document.addEventListener('shopify:section:load', mount, { signal });
  document.addEventListener('shopify:section:unload', mount, { signal });
  document.addEventListener('shopify:section:reorder', mount, { signal });
  document.addEventListener('shopify:section:select', requestStateSync, { signal });
  if (window.DivettaZorge?.subscribe) unsubscribeStateFrame = window.DivettaZorge.subscribe('media-autonomous-states', syncAutonomousStages);
  else window.addEventListener('scroll', requestStateSync, { passive: true, signal });
  window.addEventListener('resize', requestStateSync, { passive: true, signal });
  window.addEventListener('pageshow', mount, { signal });
  reduceMotion.addEventListener?.('change', () => {
    requestStateSync();
    requestVideoSync();
  }, { signal });
  connection?.addEventListener?.('change', () => {
    requestStateSync();
    requestVideoSync();
  }, { signal });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true, signal });
  else mount();

  window[KEY] = {
    mount,
    refresh: mount,
    destroy() {
      controller.abort();
      observer?.disconnect();
      unsubscribeStateFrame?.();
      if (videoFrame) window.cancelAnimationFrame(videoFrame);
      if (stateFrame) window.cancelAnimationFrame(stateFrame);
      allVideos().forEach(pause);
      delete window[KEY];
    }
  };
})();
